// God-tier Sidebar — Framer motion active indicator, hover expand, premium icon treatment
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useIsSupervisorOrAdmin, useIsAdmin } from '@/context/useRoles'
import ShiftClockWidget from '@/components/shifts/ShiftClockWidget'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard, Users, Phone, MessageSquare, UserCheck,
  BarChart3, Calendar, Settings, LogOut, Swords, Tv,
  Zap, Trophy, BookOpen, MessageCircle, Brain, FileText, UserCircle
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

interface NavItem {
  to: string
  icon: React.ReactNode
  label: string
  adminOnly?: boolean
  supervisorOnly?: boolean
  section?: string
  id?: string
}

const NAV_ITEMS: NavItem[] = [
  // Agent tools
  { to: '/',            icon: <LayoutDashboard size={17} strokeWidth={1.5} />, label: 'Overview' },
  { to: '/leads',       icon: <Users size={17} strokeWidth={1.5} />,           label: 'Leads' },
  { to: '/calls',       icon: <Phone size={17} strokeWidth={1.5} />,           label: 'Calls' },
  { to: '/sms',         icon: <MessageSquare size={17} strokeWidth={1.5} />,   label: 'SMS' },
  { to: '/chat',        icon: <MessageCircle size={17} strokeWidth={1.5} />,   label: 'App/Web Chat', id: 'chat' },
  { to: '/team-chat',   icon: <MessageSquare size={17} strokeWidth={1.5} />,   label: 'Team Chat' },
  { to: '/leaderboard', icon: <Trophy size={17} strokeWidth={1.5} />,          label: 'Leaderboard' },
  { to: '/scripts',     icon: <BookOpen size={17} strokeWidth={1.5} />,        label: 'Scripts' },
  // Supervisor tools
  { to: '/shifts',      icon: <Calendar size={17} strokeWidth={1.8} />,        label: 'Shifts',     supervisorOnly: true },
  // Admin tools
  { to: '/agents',      icon: <UserCheck size={17} strokeWidth={1.8} />,       label: 'Agents',     adminOnly: true, section: 'admin' },
  { to: '/settings',    icon: <Settings size={17} strokeWidth={1.8} />,        label: 'Settings',   adminOnly: false },
  { to: '/hr',          icon: <FileText size={17} strokeWidth={1.8} />,        label: 'HR',         adminOnly: false },
  { to: '/profile',     icon: <UserCircle size={17} strokeWidth={1.8} />,      label: 'Profile',    adminOnly: false },
]

const SECTION_LABELS: Record<string, string> = {
  ops:   'Operations',
  admin: 'Admin',
}

export default function Sidebar() {
  const { signOut, agent } = useAuth()
  const isAdmin             = useIsAdmin()
  const isSupervisorOrAdmin = useIsSupervisorOrAdmin()
  const navigate            = useNavigate()
  const location            = useLocation()
  const [escalatedCount, setEscalatedCount] = useState(0)

  // Request Notification Permission
  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  useEffect(() => {
    // Initial fetch
    supabase
      .from('support_conversations')
      .select('id', { count: 'exact', head: true })
      .eq('escalation_required', true)
      .is('claimed_by', null)
      .then(({ count }) => count !== null && setEscalatedCount(count))

    // Realtime subscription for escalations
    const sub = supabase.channel('sidebar_chat_badge')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_conversations' }, (payload) => {
        // Only ping if a NEW escalation arrived
        if (payload.eventType === 'INSERT' || (payload.eventType === 'UPDATE' && payload.new.escalation_required && !payload.old.escalation_required)) {
            if (!payload.new.claimed_by) {
               // Flash title, play sound, show OS notification
               document.title = '(1) New Escalation - Callro'
               const handleFocus = () => { document.title = 'Callro Agent Portal'; window.removeEventListener('focus', handleFocus) }
               window.addEventListener('focus', handleFocus)
               
               const audio = new Audio('/ping.mp3') // Assuming we have a standard ping or browser default
               audio.play().catch(() => {})

               if (Notification.permission === 'granted') {
                 new Notification('New Shield Escalation', {
                   body: 'A user requires human assistance.',
                 })
               }
            }
        }
        
        supabase
          .from('support_conversations')
          .select('id', { count: 'exact', head: true })
          .eq('escalation_required', true)
          .is('claimed_by', null)
          .then(({ count }) => count !== null && setEscalatedCount(count))
      })
      .subscribe()

    // Realtime subscription for claimed messages
    const msgSub = supabase.channel('global_messages')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'support_messages' }, async (payload) => {
         if (payload.new.sender_type === 'user' && agent) {
            const { data: conv } = await supabase.from('support_conversations').select('claimed_by').eq('id', payload.new.conversation_id).single()
            if (conv?.claimed_by === agent.id) {
               document.title = '(1) New Message - Callro'
               const handleFocus = () => { document.title = 'Callro Agent Portal'; window.removeEventListener('focus', handleFocus) }
               window.addEventListener('focus', handleFocus)
               
               const audio = new Audio('/ping.mp3')
               audio.play().catch(() => {})
               if (Notification.permission === 'granted') {
                 new Notification('New Message', {
                   body: 'A user replied to your ongoing conversation.',
                 })
               }
            }
         }
      })
      .subscribe()

    return () => { 
      supabase.removeChannel(sub)
      supabase.removeChannel(msgSub)
    }
  }, [agent])

  async function handleSignOut() {
    await signOut()
    toast.success('Signed out')
    navigate('/login')
  }

  const visibleItems = NAV_ITEMS.filter(item => {
    if (item.adminOnly      && !isAdmin)             return false
    if (item.supervisorOnly && !isSupervisorOrAdmin) return false
    return true
  })

  const initials = agent?.name
    ?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) ?? '?'

  let lastSection = ''

  return (
    <aside className="sidebar" aria-label="Main navigation">

      {/* Logo area */}
      <div className="sidebar-logo">
        <img
          src="/callro_icon.png"
          alt="Callro"
          style={{
            width: '2rem', height: '2rem',
            borderRadius: '0.5rem',
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
        <img
          src="/callro_logo.png"
          alt="Callro"
          className="sidebar-agent-name"
          style={{ height: '1.25rem', width: 'auto', objectFit: 'contain' }}
        />
      </div>

      <div style={{
        width: 'calc(100% - 1.5rem)',
        height: '1px',
        background: 'var(--border-subtle)',
        margin: '0 0.75rem 0.375rem',
        flexShrink: 0,
      }} />

      {/* Nav */}
      <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', flex: 1, width: '100%', paddingBottom: '0.5rem' }}>
        {visibleItems.map(item => {
          const showSection = item.section && item.section !== lastSection
          if (item.section) lastSection = item.section
          const isActive = location.pathname.startsWith(item.to)

          return (
            <div key={item.to} style={{ position: 'relative' }}>
              {showSection && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 0.8125rem 0.25rem',
                  overflow: 'hidden',
                }}>
                  <div style={{ width: '1.125rem', flexShrink: 0 }} />
                  <span className="sidebar-label" style={{
                    fontSize: '0.5625rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    opacity: 0,
                  }}>
                    {SECTION_LABELS[item.section!]}
                  </span>
                </div>
              )}
              
              <NavLink
                to={item.to}
                className={`sidebar-item relative z-10 ${isActive ? 'text-primary' : ''}`}
                aria-label={item.label}
                id={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                style={{ background: 'transparent' }} // override the CSS hover/active background since we use Framer Motion now
              >
                {/* The glowing active line (left) */}
                {isActive && (
                  <motion.div
                    layoutId="sidebar-active-line"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: '0.375rem',
                      bottom: '0.375rem',
                      width: '2px',
                      background: 'var(--brand-primary)',
                      borderRadius: '0 2px 2px 0',
                      boxShadow: '0 0 8px rgba(37,99,235,0.6)',
                    }}
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
                {/* The active pill background */}
                {isActive && (
                  <motion.div
                    layoutId="sidebar-active-bg"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: 'var(--radius-md)',
                      background: 'rgba(37,99,235,0.10)',
                      zIndex: -1,
                    }}
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
                
                <motion.span whileHover={{ scale: 1.1, rotate: -3 }} whileTap={{ scale: 0.95 }} className="sidebar-icon z-10" style={{ position: 'relative', color: isActive ? 'inherit' : '#3B82F6' }}>
                  {item.icon}
                  {item.id === 'chat' && escalatedCount > 0 && (
                     <span style={{
                       position: 'absolute', top: '-4px', right: '-4px',
                       background: '#EF4444', color: 'white', borderRadius: '50%',
                       minWidth: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                       fontSize: '9px', fontWeight: 700, padding: '0 3px',
                       boxShadow: '0 0 0 2px var(--bg-default)', zIndex: 20
                     }}>
                       {escalatedCount}
                     </span>
                  )}
                </motion.span>
                <span className="sidebar-label z-10" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1, color: isActive ? 'inherit' : 'var(--text-secondary)', fontWeight: 500 }}>
                  {item.label}
                </span>
              </NavLink>
            </div>
          )
        })}
      </nav>

      {/* Bottom section */}
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '0.125rem' }}>
        <div style={{
          width: 'calc(100% - 1.5rem)',
          height: '1px',
          background: 'var(--border-subtle)',
          margin: '0 0.75rem 0.375rem',
          flexShrink: 0,
        }} />

        <div style={{ padding: '0 0.8125rem', display: 'flex', alignItems: 'center', gap: '0.625rem', minHeight: '2.25rem', overflow: 'hidden' }}>
          <div className="sidebar-icon" style={{ color: '#3B82F6', flexShrink: 0 }}>
            <ShiftClockWidget compact />
          </div>
          <span className="sidebar-label" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontFamily: "'JetBrains Mono', monospace" }}>
            Shift
          </span>
        </div>

        <div style={{ padding: '0 0.8125rem', display: 'flex', alignItems: 'center', gap: '0.625rem', minHeight: '2.5rem', overflow: 'hidden' }}>
          <div title={agent ? `${agent.name} — ${agent.role}` : ''} style={{
            width: '1.875rem', height: '1.875rem', borderRadius: '50%',
            background: 'linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)',
            border: '1px solid rgba(37,99,235,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.625rem', fontWeight: 700, color: '#fff',
            fontFamily: "'Inter', sans-serif", flexShrink: 0, letterSpacing: '0.02em',
          }}>
            {initials}
          </div>
          <div className="sidebar-agent-name" style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', minWidth: 0 }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.005em' }}>
              {agent?.name ?? 'Agent'}
            </span>
            <span style={{ fontSize: '0.625rem', fontWeight: 500, color: 'var(--text-muted)', textTransform: 'capitalize', letterSpacing: '0.02em' }}>
              {agent?.role ?? '—'}
            </span>
          </div>
        </div>

        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleSignOut} className="sidebar-item" aria-label="Sign out" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', width: 'auto' }}>
          <span className="sidebar-icon"><LogOut size={15} strokeWidth={1.5} style={{ color: '#3B82F6' }} /></span>
          <span className="sidebar-label" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 500 }}>Sign out</span>
        </motion.button>

        <div style={{ padding: '0.3125rem 0.8125rem 0.5rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <span className="relative flex h-[6px] w-[6px]">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-[6px] w-[6px] bg-green-500"></span>
          </span>
          <span className="sidebar-label" style={{ fontSize: '0.5625rem', fontWeight: 600, color: '#22C55E', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Live
          </span>
        </div>
      </div>
    </aside>
  )
}
